import { Pencil, ShieldAlert, ShieldCheck, Trash2, Truck } from 'lucide-react'
import { getFileUrl } from '@/clients/http'

import { useDeliveryMethodsTable } from '@/hooks/features/delivery-methods'
import { useDataTable } from '@/hooks/ui'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import { ActionDropdown, ConfirmDialog, PageHeader } from '@/components/atoms'

import { useBusiness } from '@/contexts/BusinessContext'

import { DeliveryMethodModel } from '@/types'

import { DeliveryMethodDialog } from './DeliveryMethodDialog'

type TableColumns = 'name' | 'type' | 'charge' | 'status' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'name',
  'type',
  'charge',
  'status',
  'actions',
]

export const DeliveryMethodsTable = () => {
  const {
    loading,
    data,
    pagination,
    stats,
    mode,
    dialogOpen,
    editMethod,
    deleteDialogOpen,
    methodToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateMethod,
    handleUpdateMethod,
    toggleStatus,
  } = useDeliveryMethodsTable()
  const { formatCurrency } = useBusiness()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    DeliveryMethodModel.DeliveryMethod,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'name',
        columnLabel: () => 'Method',
        cell: (data) => (
          <div className="flex items-center gap-3">
            {data.logo ? (
              <img
                src={getFileUrl(data.logo)}
                alt={data.name}
                className="size-8 rounded-md border border-border-subtle bg-card object-contain p-1"
              />
            ) : (
              <div className="flex size-8 items-center justify-center rounded-md bg-subdued">
                <Truck className="size-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-ink">{data.name}</div>
              <div className="text-sm text-text-2">{data.key}</div>
            </div>
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'type',
        columnLabel: () => 'Type',
        cell: (data) => (
          <Badge tone="soft" className="capitalize">
            {data.type}
          </Badge>
        ),
      },
      {
        accessorKey: 'charge',
        columnLabel: () => 'Charge',
        cell: (data) => (
          <span className="tabular-nums text-ink">
            {formatCurrency(typeof data.charge === 'number' ? data.charge : 0)}
          </span>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        hasSorting: true,
      },

      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <Badge tone={data.status === 'active' ? 'success' : 'soft'}>
            {data.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'Configure',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        size="large"
        title="Delivery methods"
        onAddClick={openCreateDialog}
        addButtonText="Add delivery method"
      />

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Couriers and manual methods</CardTitle>
              <CardDescription>
                {stats.totalMethods} {stats.totalMethods === 1 ? 'method' : 'methods'}, {stats.activeMethods} active,{' '}
                {stats.inactiveMethods} disabled
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <ColumnVisibilityDropdown
                columns={columns}
                onColumnVisibilityChange={changeColumnVisibility}
                disabledColumns={disabledColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No delivery methods found"
              btnText="Add delivery method"
              openCreateDialog={openCreateDialog}
            />
          ) : (
            <>
              <DataTable columns={columns} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delivery Method Dialog */}
      <DeliveryMethodDialog
        open={dialogOpen}
        mode={mode}
        editMethod={editMethod}
        closeDialog={closeDialog}
        onCreate={handleCreateMethod}
        onUpdate={handleUpdateMethod}
        loading={createLoading || updateLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete delivery method?"
        description={`"${methodToDelete?.name}" will be permanently deleted. This can't be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
